<template>

  <div class="tw-flex tw-flex-col tw-gap-6">

    <div class="card" v-if="isAgency || isEnterprise">
      <div class="card-body">
        <div class="tw-grid tw-grid-cols-3 tw-gap-5">
          <b-form-group label="Purchase Order Number">
            <b-input v-model="poNumber"></b-input>
          </b-form-group>

          <b-form-group label="Job ID">
            <b-input v-model="jobId"></b-input>
          </b-form-group>

          <template v-if="isEnterprise">
            <b-form-group label="Licensee">
              <b-input readonly v-model="licensee"></b-input>
            </b-form-group>
          </template>
          <template v-else-if="isAgency">
            <b-form-group label="Client / Licensee">
              <v-select v-model="licensee"
                        @search="fetchLicensees"
                        :options="licenseesOptions"
                        push-tags
                        taggable
                        :filterable="false">
                <template #no-options>
                  type to search licensee...
                </template>
                <template #option="lic">
                  {{ lic.label }}
                </template>
                <template #selected-option="lic">
                  {{ lic.label }}
                </template>
              </v-select>
            </b-form-group>
          </template>

        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div v-if="transactionCompleted">
          <div class="bg-light p-3 text-center">
            <div v-if="assetDownloadLoading">
              Please wait, processing ...
            </div>
            <div v-else-if="assetDownloadError">
              {{ assetDownloadError }}
            </div>
            <div v-else-if="assetDownloadUrl">
              Congratulations, the transaction was completed.
              <a :href="assetDownloadUrl">Click here to download</a>
            </div>
          </div>
        </div>


        <form ref="purchaseForm" @submit="submit" v-else>
          <h5 class="mb-4">Billing & Payment Information</h5>
          <div class="row">
            <div class="col-12 col-xl-6 pr-0 pr-xl-4">


              <div v-if="paymentMethods.length > 0">
                <payment-method-option v-for="method in paymentMethods"
                                       :payment-method="method"
                                       :disabled="saving"
                                       v-model="selectedPaymentMethod"
                                       class="mb-1"/>

                <payment-method-option :payment-method="newCardPaymentMethod"
                                       v-model="selectedPaymentMethod"
                                       :disabled="saving"
                                       class="mb-2"/>
              </div>

              <div v-if="selectedPaymentMethod === newCardPaymentMethod">
                <payment-card-form ref="stripeCardForm" :disabled="saving"/>
                <organization-billing-form-inputs :organization="organization"
                                                  @organizationParamsChange="organizationAddress = arguments[0]"
                                                  @stripeParamsChange="stripeAddress = arguments[0]"/>
              </div>

              <b-alert variant="danger" :show="error !== null" class="mb-3">{{ error }}</b-alert>


            </div>
            <div class="col-12 col-xl-6 pr-0 pl-xl-4 tw-flex tw-flex-col tw-gap-5">
              <div class="tw-flex tw-justify-between">
                <div class="tw-font-medium">Subtotal</div>
                <div class="tw-font-medium">${{ amount / 100 }}</div>
              </div>
              <div class="border-top"></div>
              <div class="tw-flex tw-justify-between">
                <div class="tw-font-medium tw-text-lg">Amount due today</div>
                <div class="tw-font-medium tw-text-lg">${{ amount / 100 }}</div>
              </div>

              <b-button variant="primary"
                        block
                        class="btn-rounded px-3 mt-2"
                        type="submit"
                        :disabled="saving || !selectedPaymentMethod || !agreement">
                Complete checkout
              </b-button>

              <agree-asset @agreement="handleAgreement" :agreement-text="agreementText"/>

              <div class="tw-border tw-border-solid tw-border-gray-200 tw-p-5">
                <span class="tw-font-medium">Secure checkout.</span>
                <span>
                For data security, we do not store your payment information on our servers. All payment data is securely stored and managed by Stripe, Inc.
              </span>
              </div>
            </div>
          </div>
        </form>
      </div>

    </div>
  </div>

</template>

<script>
import PaymentMethodOption from "./PaymentMethodOption";
import PaymentCardForm from "./PaymentCardForm";
import OrganizationBillingFormInputs from "../Billing/OrganizationBillingFormInputs";
import POCConfig from "../../../../config.js.erb";
import AgreeAsset from "../Onboarding/AgreeAsset.vue";
import {PaymentsApi} from "../../Api/PaymentsApi.js";
import GeneralMixins from "../../../mixins/GeneralMixins.js";
import {OrganizationsApi} from "../../Api/OrganizationsApi.js";
import vSelect from "vue-select";

export default {
  components: {vSelect, AgreeAsset, OrganizationBillingFormInputs, PaymentCardForm, PaymentMethodOption},
  props: ['paymentMethods', 'clientSecret', 'successUrl', 'payment', 'organization', 'amount'],
  mixins: [GeneralMixins],

  data: function () {
    return {
      error: null,
      newCardPaymentMethod: "newCard",
      selectedPaymentMethod: null,
      saving: false,
      transactionCompleted: this.payment.status === 'succeeded',
      stripeAddress: null,
      agreement: false,
      licenseAgreementModal: false,
      agreementText: null,
      organizationAddress: null,

      assetDownloadLoading: false,
      assetDownloadUrl: null,
      assetDownloadError: null,
      assetDownloadAttempts: 0,

      poNumber: null,
      licensee: null,
      jobId: null,
      licenseesOptions: []
    }
  },

  async mounted() {
    this.poNumber = this.payment.provisioning_data.po_number
    this.licensee = this.payment.provisioning_data.licensee
    this.jobId = this.payment.provisioning_data.job_id

    this.agreementText = await PaymentsApi.getLicenseAgreementText(this.payment.id)
  },

  watch: {
    selectedPaymentMethod: function () {
      this.error = null;
    }
  },

  created() {
    this.selectedPaymentMethod = this.defaultSelectedPaymentMethod
  },

  methods: {
    async getLicenseAvailable() {
      this.assetDownloadLoading = true
      this.assetDownloadAttempts += 1
      this.assetDownloadError = null

      try {
        const data = await PaymentsApi.getLicenseAvailable(this.payment.id)
        this.assetDownloadUrl = `/asset_downloads/${data.asset_id}?version_size=${data.version_size}`
        this.assetDownloadLoading = false
      } catch (e) {
        if (this.assetDownloadAttempts > 10) {
          this.assetDownloadLoading = false
          this.assetDownloadError = 'Unable to create a download link for this asset. Please contact support.'
          return
        }
        setTimeout(() => {
          this.getLicenseAvailable()
        }, 1000)
      }
    },

    fetchLicensees(search, loading) {
      loading(true)
      this.searchLicensees(loading, search, this)
    },

    searchLicensees: _.debounce((loading, search, vm) => {
      const url = `/customers/licensees?query=${encodeURIComponent(search)}`
      vm.$http.get(url).then(fulfilled => {
        vm.licenseesOptions = fulfilled.data
        loading(false)
      })
    }, 150),

    handlePaymentMethodSelect: function (paymentMethod) {
      this.selectedPaymentMethod = paymentMethod;
    },

    handleAgreement: function (agreement) {
      this.agreement = agreement
    },

    async payWithExistingCard(stripeId) {
      const stripe = Stripe(POCConfig.STRIPE_PUBLISHABLE_KEY);
      await this.pay(stripe, {
        payment_method: stripeId
      })
    },

    async payWithNewCard() {
      const name = this.$refs.stripeCardForm.ccName;
      const stripe = this.$refs.stripeCardForm.stripe;
      const card = this.$refs.stripeCardForm.card;

      if (!stripe) {
        throw new Error('Stripe component not present')
      }

      if (name.trim() === '') {
        throw new Error('Name is required.')
      }

      await this.updateBillingAddress()

      const result = await this.createPaymentMethod(stripe, card, name)
      const paymentMethodId = result.paymentMethod.id

      await this.pay(stripe, {
        payment_method: paymentMethodId
      })

      // this is direct payment, without saving the payment method
      // await this.pay(stripe, {
      //   payment_method: {
      //     card: card,
      //     billing_details: {
      //       name: name,
      //       address: this.stripeAddress
      //     }
      //   }
      // })
    },

    async updatePaymentProvisioningData() {
      if (this.isEnterprise || this.isAgency) {
        await PaymentsApi.updatePaymentPurchaseRequestData(this.payment.id, {
          po_number: this.poNumber,
          licensee: this.licensee,
          job_id: this.jobId
        })
      }
    },

    async updateBillingAddress() {
      if (this.hasAddress) {
        return
      }

      if (this.organizationAddress) {
        await this.$http.patch("/customers/organization", this.organizationAddress)
      }
    },

    async createPaymentMethod(stripe, card, name) {
      const result = await this.createPaymentMethodStripeWrapper(stripe, card, name)
      await this.createPocstockPaymentMethod(result.paymentMethod.id)
      return result
    },

    async createPocstockPaymentMethod(stripePaymentMethodId) {
      await this.$http.post('/customers/payment_methods', {
        payment_method: {
          stripe_id: stripePaymentMethodId
        }
      })
    },

    async createPaymentMethodStripeWrapper(stripe, card, name) {
      return new Promise((resolve, reject) => {
        stripe.createPaymentMethod({
          type: 'card',
          card: card,
          billing_details: {
            name: name,
            address: this.stripeAddress
          }
        }).then((result) => {
          if (result.error && result.error.message) {
            reject(new Error(result.error.message))
          } else {
            resolve(result)
          }
        })
      })
    },

    async pay(stripe, data) {
      return new Promise((resolve, reject) => {
        stripe.confirmCardPayment(this.clientSecret, data).then((result) => {
          if (result.error) {
            reject(result.error.message)
          } else {
            if (result.paymentIntent.status === 'succeeded') {
              resolve()
            } else {
              reject(new Error('Payment failed - intent was not marked as succeeded.'))
            }
          }
        })
      })
    },

    async submit(e) {
      e.preventDefault()
      if (!this.$refs.purchaseForm.checkValidity()) {
        return
      }

      if (this.isAgency || this.isEnterprise) {
        if (!this.licensee) {
          this.error = 'Please select a licensee.'
          return
        }
      }

      if (!this.selectedPaymentMethod) return;

      this.saving = true
      try {
        await this.updatePaymentProvisioningData()

        if (this.selectedPaymentMethod === this.newCardPaymentMethod) {
          await this.payWithNewCard()
        } else {
          await this.payWithExistingCard(this.selectedPaymentMethod.stripe_id)
        }
      } catch (e) {
        this.error = e.message
      } finally {
        this.saving = false
      }

      if (!this.error) {
        this.transactionCompleted = true
        await this.getLicenseAvailable()
      }
    },
  },

  computed: {
    isAgency() {
      return this.organization && this.organization.organization_type === 'agency'
    },

    isEnterprise() {
      return this.organization && this.organization.organization_type === 'enterprise'
    },

    hasAddress() {
      if (this.organization) {
        return [
          this.organization.billing_address_line_1,
          this.organization.billing_address_line_2,
          this.organization.billing_city,
          this.organization.billing_state,
          this.organization.billing_country,
          this.organization.billing_zip
        ].some(v => v !== null && v !== '')
      } else {
        return false
      }
    },
    defaultSelectedPaymentMethod: function () {
      if (this.paymentMethods.length === 0) return this.newCardPaymentMethod;
      return null;
    }
  }
}
</script>

<style>
@tailwind base;
@tailwind utilities;
</style>